import "./styles.scss";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Dropdown, PaginationProps } from "semantic-ui-react";
import moment from "moment";
import TableCustom from "../../../../../../components/Table";
import ButtonCustom from "../../../../../../components/Button";
import DropdownCustom from "../../../../../../components/DropdownCustom";
import SearchWithDebounce from "../../../../../../components/SearchBar4Table/SearchWithDebounce";
import { SWAL_DELETE_OPTION } from "../../../../../../components/SweetAlert/configs/SwalConfigs";
import { SweetAlertCommon } from "../../../../../../components/SweetAlert";
import { toastError, toastSuccess } from "../../../../../../components/Toast";
import {
  deleteUserPropertyRestrictionService,
  getRestrictionErrorMessage,
  getUserPropertyRestrictionsService,
  UserPropertyRestriction,
  UserPropertyRestrictionList,
} from "../../../../services/UserPropertyRestrictionServices";
import ModalBlockUser from "./Components/ModalBlockUser";

const ITEM_PER_PAGE = 25;

const BlockedUsersTab = () => {
  const { id: propertyId } = useParams();
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [list, setList] = useState<UserPropertyRestrictionList | null>(null);
  const [filters, setFilters] = useState({ page: 1, keyword: "" });
  const [modal, setModal] = useState<{
    open: boolean;
    restriction: UserPropertyRestriction | null;
  }>({
    open: false,
    restriction: null,
  });

  useEffect(() => {
    _fetchList();
  }, [filters, refresh]);

  const _fetchList = async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const response = await getUserPropertyRestrictionsService({
        property_id: propertyId,
        keyword: filters.keyword,
        page: filters.page,
        item_per_page: ITEM_PER_PAGE,
      });
      setList(response?.data);
    } catch (error) {
      toastError(getRestrictionErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const _reload = () => setRefresh(!refresh);

  const _onUnblock = (restriction: UserPropertyRestriction) => {
    const options = SWAL_DELETE_OPTION({
      title: `Unblock ${
        restriction?.user?.name || restriction?.user?.email
      } at this property?`,
      icon: null,
      confirmText: "Unblock",
      confirmClass: "danger",
    });
    SweetAlertCommon({
      options,
      confirmCallback: async () => {
        try {
          setLoading(true);
          await deleteUserPropertyRestrictionService(restriction.id);
          toastSuccess("User unblocked");
          _reload();
        } catch (error) {
          toastError(getRestrictionErrorMessage(error));
          setLoading(false);
        }
      },
      cancelCallback: () => null,
    });
  };

  const _formatDate = (value: UserPropertyRestriction["created_at"]) => {
    if (!value) return "-";
    const date = typeof value === "number" ? moment.unix(value) : moment(value);
    return date.isValid() ? date.format("DD/MM/YYYY HH:mm") : "-";
  };

  const _flag = (value: boolean) =>
    value ? <i className="uil uil-check" style={{ fontSize: "20px" }} /> : "–";

  const columns = [
    "User",
    "Hot desk",
    "Meeting room",
    "Day office",
    "Reason",
    "Created at",
    "Action",
  ];

  const decorateDataTable = () => {
    if (!list?.items?.length) return [];
    return list.items.map((restriction) => ({
      user: (
        <div className={"blocked-users-user"}>
          <span>{restriction?.user?.name || "-"}</span>
          <span className={"email"}>{restriction?.user?.email}</span>
        </div>
      ),
      block_hotdesk: _flag(restriction?.block_hotdesk),
      block_meeting_room: _flag(restriction?.block_meeting_room),
      block_day_office: _flag(restriction?.block_day_office),
      reason: restriction?.reason || "-",
      created_at: _formatDate(restriction?.created_at),
      action: (
        <DropdownCustom
          direction={"left"}
          icon={null}
          trigger={
            <ButtonCustom
              style={{ width: "38px", height: "38px" }}
              className={"primary-alt"}
            >
              <i className="uil uil-setting" style={{ fontSize: "21px" }} />
            </ButtonCustom>
          }
        >
          <Dropdown.Menu>
            <Dropdown.Item
              onClick={() => setModal({ open: true, restriction })}
              text={"Edit"}
              icon={
                <i className="uil uil-edit icon" style={{ fontSize: "16px" }} />
              }
            />
            <Dropdown.Item
              onClick={() => _onUnblock(restriction)}
              text={"Unblock"}
              icon={
                <i
                  className="uil uil-unlock icon"
                  style={{ fontSize: "16px" }}
                />
              }
            />
          </Dropdown.Menu>
        </DropdownCustom>
      ),
    }));
  };

  return (
    <div className={"deskimo-tab-content"}>
      <div className={"blocked-users-toolbar"}>
        <SearchWithDebounce
          initialValue={filters.keyword}
          placeholder={"Search by user name or email"}
          onChange={(keyword) =>
            setFilters((prev) =>
              prev.keyword === keyword ? prev : { page: 1, keyword }
            )
          }
        />
        <ButtonCustom
          onClick={() => setModal({ open: true, restriction: null })}
          height={"36px"}
          className={"mr-0 primary"}
        >
          Block user +
        </ButtonCustom>
      </div>
      <TableCustom
        tableData={decorateDataTable()}
        showTotal
        loading={loading}
        columns={columns}
        itemPerPage={list?.pagination?.limit || ITEM_PER_PAGE}
        currentPage={filters.page}
        totalItems={list?.pagination?.total}
        unit={"users"}
        emptyText={"No blocked users"}
        onChangePagination={(
          e: React.MouseEvent<HTMLAnchorElement>,
          { activePage }: PaginationProps
        ) => setFilters({ ...filters, page: Number(activePage) })}
      />
      <ModalBlockUser
        open={modal.open}
        restriction={modal.restriction}
        propertyId={propertyId || ""}
        onClose={() => setModal({ open: false, restriction: null })}
        onSaved={() => {
          setModal({ open: false, restriction: null });
          _reload();
        }}
      />
    </div>
  );
};

export default BlockedUsersTab;
